/**
 * @fileoverview What a bed is, in the bed dialog (D55; owner, 2026-10-16:
 * "its better to ask about the cover at the time of set up of the bed or let
 * them change later"), reworked after a design critique (owner, 2026-10-17:
 * "I don't like how there's more information hidden, and also some options
 * should be turned off ... there's contradictions in there"; "yes and push").
 *
 * Nothing is folded away: every question the bed's kind asks shows, and only
 * those (the server's table, GET /locations/bed-questions; owner,
 * 2026-10-16: "if its a greenhouse dont ask about covers"):
 *
 * - Its kind, one choice in groups (Outdoors, Pots, Under cover, Indoors),
 *   each with its hint: listed at once when adding a bed, as one row with
 *   Change (opening the list in a sheet) when editing one.
 * - A kind with fewer questions says why (a greenhouse: "the glass or plastic
 *   is its cover").
 * - Its sun, one choice in a row that wraps at a large text size.
 * - What blocks the sun (D57; design rev 2, the Fable critique), inside the
 *   sun question's block, under its hint: a one-choice list (Trees, A
 *   building or fence, Not sure), each source with a line on what it does.
 *   Only where the server says the kind asks it (questions[kind].shade) and
 *   the bed is not in full sun. Not sure is null, every bed's answer until
 *   one is chosen.
 * - What's over it, one choice: Nothing, Hoops ready for a cloth, Plastic on
 *   hoops all season (which is a tunnel: Grow offers to make it one). It was
 *   three questions that could contradict each other (a cover that stays,
 *   hoops, and Plastic on hoops among the covers).
 * - Frost covers on hand, tick what you have (a greenhouse: the layer kept
 *   inside for a hard freeze), in two columns on a wide screen; nothing ticked
 *   is nothing said.
 * - Shade cloth on hand, one choice (None, 30%, 50%), for a sunny bed only.
 * - How it's watered, one choice.
 * - Wind, one choice: Out of the wind or Windy spot ("Sheltered" meant a bed
 *   the weather does not reach elsewhere).
 *
 * The bed's kind (a change of kind would change its shelter), its sun, what blocks
 * the sun (D57) and what's over it (whether it is sheltered) are the garden
 * owner's (B37; owner, 2026-10-20, "b37 yes"): given siteLockedReason (a
 * member editing a bed in someone else's garden), those are shown greyed
 * (the kind's Change row too, and its list does not open), with the reason
 * once ("Only the garden's owner can change this."), and the
 * offer to make it a tunnel is not made; the rest stays the member's to edit.
 *
 * One choice looks like one (a round mark, or a pressed segment); several
 * like several (a square tick). An answer hidden by a change of kind is kept
 * in the draft, so switching back brings it back; only what the kind asks is
 * sent (bedFormFields), and the server keeps only that. So does what blocks
 * the sun when the sun goes to Full and back (no confirmation: the tap costs
 * nothing to undo).
 *
 * Where: EditLocationDialog (Add bed, Edit bed).
 * API: none of its own; the dialog reads the questions (useBedQuestions) and
 * sends the answers with the bed.
 * Words (en/es): locations.facts.* for the field labels, hints, notes, the
 * shade cloth's None, 30% and 50%, and the shade sources' lines
 * (shadeSourceSub.*). The names of the choices themselves (the kinds, the sun,
 * the covers, the watering, the wind, and "What blocks the sun?" with its
 * sources and Not sure, beds.form.shade.*) are the server's, sent with the
 * questions (words).
 *
 * @module BedFactsFields
 */

import { retryRule } from '@/config/queryRetry';
import React from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { useTranslation } from '@/shared/hooks/useTranslation';
import {
  getBedQuestions, type BedCover, type BedFacts, type BedOverChoice, type BedKind, type BedQuestions,
} from '@/shared/api/locations';

/** The bed form's answers as they are being chosen: the server's shape. */
export type BedDraft = BedFacts;

/**
 * Full sun's key: the one sun level never asked what blocks it (the server's applicableFacts). A key the server
 * sends, as NEW_BED_DRAFT's sun is, not a value of its own.
 */
const FULL_SUN: BedDraft['sun'] = 'full';

/** A new bed's answers before any is chosen: in the ground, full sun, nothing else said (the server's defaults). */
export const NEW_BED_DRAFT: BedDraft = {
  kind: 'ground', sun: FULL_SUN, covers: null, coverStays: false, hoops: false, watering: null, wind: null, shade: null,
};

/**
 * Whether "What blocks the sun?" is asked of the draft: the server says its kind asks it (and its sun), it is not in
 * full sun, and the server could name the sources (none read: nothing is asked, and the stored answer is kept).
 *
 * @param draft - The answers.
 * @param questions - The server's table.
 * @returns True when the question shows and its answer is sent.
 */
function shadeSourceAsked(draft: BedDraft, questions: BedQuestions): boolean {
  const asks = questions.questions[draft.kind];
  return Boolean(asks?.sun && asks.shade) && draft.sun !== FULL_SUN
    && questions.shade.length > 0 && Boolean(questions.words.shade);
}

/** The query key the bed questions are kept under: they change only with the server. */
const BED_QUESTIONS_KEY = ['bed-questions'];

/**
 * The bed form's questions, read once per language and kept (the choices' names are said in the reader's language).
 * Read only while the form is open (W24 Job H): Beds no longer reads them on every visit, only when Add bed or Edit bed
 * opens.
 *
 * @param enabled - Whether the form is open and wants them now; true by default.
 * @returns The query: data is BedQuestions once read.
 */
export function useBedQuestions(enabled = true) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  return useQuery({ queryKey: [...BED_QUESTIONS_KEY, lang], queryFn: () => getBedQuestions(lang), staleTime: Infinity, retry: retryRule(1), enabled });
}

/**
 * The answers to send with the bed (multipart fields), only those its kind
 * asks: kind; sun; covers ("none" for none on hand, empty for not said);
 * cover_stays, hoops; watering and wind (empty for not said); shade, what
 * blocks the sun, only while it is asked (empty for Not sure). The draft keeps
 * a shade answer the sun has hidden; it is not sent, and the server saves
 * none for a bed in full sun.
 *
 * @param draft - The answers.
 * @param questions - The server's table.
 * @returns Field name and value pairs.
 */
export function bedFormFields(draft: BedDraft, questions: BedQuestions): Array<[string, string]> {
  const asks = questions.questions[draft.kind];
  const out: Array<[string, string]> = [['kind', draft.kind]];
  if (!asks) return out;
  if (asks.sun) out.push(['sun', draft.sun]);
  if (shadeSourceAsked(draft, questions)) out.push(['shade', draft.shade ?? '']);
  if (asks.covers.length > 0) {
    const covers = draft.covers === null ? null : draft.covers.filter((cover) => asks.covers.includes(cover));
    out.push(['covers', covers === null ? '' : covers.length === 0 ? 'none' : covers.join(',')]);
  }
  if (asks.coverStays) out.push(['cover_stays', draft.coverStays ? '1' : '0']);
  if (asks.hoops) out.push(['hoops', draft.hoops ? '1' : '0']);
  if (asks.watering) out.push(['watering', draft.watering ?? '']);
  if (asks.wind) out.push(['wind', draft.wind ?? '']);
  return out;
}

/**
 * A question's label and its hint.
 *
 * @param props.id - The label's id, which its choices are named by.
 * @param props.label - The question.
 * @param props.hint - A line under it, or none.
 */
const Question: React.FC<{ id: string; label: string; hint?: string | null }> = ({ id, label, hint = null }) => (
  <div>
    <p id={id} className="text-sm font-semibold">{label}</p>
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

/** What a one-choice row of segments is given. */
interface SegmentedProps {
  /** The id of the question's label, which names the row. */
  labelId: string;
  /** The choices in order: each one's value and its words. */
  options: Array<{ value: string; label: string }>;
  /** The value chosen, or null for none. */
  value: string | null;
  /** A choice was tapped. */
  onChoose: (value: string) => void;
  /** Nothing can be changed (a save under way). */
  disabled?: boolean;
}

/**
 * A short one-choice question as a row of segments. Each segment is as wide as its longest words need (in rem, so a
 * large text size widens them), and the row wraps when they do not fit: "None 30% 50%" stays one row, "Soaker hose or
 * drip" takes two.
 *
 * @param props - SegmentedProps.
 */
const Segmented: React.FC<SegmentedProps> = ({ labelId, options, value, onChoose, disabled = false }) => {
  const longest = Math.max(...options.map((option) => option.label.length));
  const min = Math.max(4, Math.round((longest * 0.38 + 0.8) * 10) / 10);
  return (
  <div role="radiogroup" aria-labelledby={labelId} className="mt-1.5 grid gap-1 rounded-lg border p-1" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}rem, 1fr))` }}>
    {options.map((option) => {
      const on = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={on}
          disabled={disabled}
          onClick={() => onChoose(option.value)}
          className={cn(
            'flex min-h-10 items-center justify-center rounded-md px-2 text-center text-sm leading-tight transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
            on ? 'bg-primary font-semibold text-primary-foreground' : 'text-foreground hover:bg-muted',
          )}
        >
          {option.label}
        </button>
      );
    })}
  </div>
  );
};

/** What a one-choice row with a hint is given. */
interface ChoiceRowProps {
  /** Its words. */
  label: string;
  /** Its second line, or none. */
  hint?: string | null;
  /** It is the one chosen. */
  on: boolean;
  /** It was tapped. */
  onChoose: () => void;
  /** Nothing can be changed (a save under way). */
  disabled?: boolean;
}

/**
 * One row of a one-choice list: a round mark, its words and its hint; the chosen one tinted.
 *
 * @param props - ChoiceRowProps.
 */
const ChoiceRow: React.FC<ChoiceRowProps> = ({ label, hint = null, on, onChoose, disabled = false }) => (
  <button
    type="button"
    role="radio"
    aria-checked={on}
    disabled={disabled}
    onClick={onChoose}
    className={cn(
      'flex min-h-11 w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
      on ? 'bg-primary/10 ring-1 ring-primary' : 'hover:bg-muted',
    )}
  >
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-primary" aria-hidden="true">
      {on && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
    </span>
    <span className="min-w-0">
      <span className={cn('block text-[0.9375rem]', on && 'font-semibold')}>{label}</span>
      {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
    </span>
  </button>
);

/**
 * One box of a pick-several list: a square tick and its words.
 *
 * @param props.label - Its words.
 * @param props.on - Ticked.
 * @param props.onToggle - Ticked or unticked.
 * @param props.disabled - Nothing can be changed.
 */
const CheckRow: React.FC<{ label: string; on: boolean; onToggle: () => void; disabled?: boolean }> = ({ label, on, onToggle, disabled = false }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={on}
    disabled={disabled}
    onClick={onToggle}
    className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
  >
    <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border-2 border-primary', on && 'bg-primary text-primary-foreground')} aria-hidden="true">
      {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
    </span>
    <span className="text-[0.9375rem]">{label}</span>
  </button>
);

/** What the fields show and change. */
export interface BedFactsFieldsProps {
  /** The server's table; nothing is drawn until it is read. */
  questions: BedQuestions | undefined;
  /** The answers. */
  value: BedDraft;
  /** An answer changed. */
  onChange: (next: BedDraft) => void;
  /** Adding a bed lists the kinds at once; editing one shows its kind as one row with Change. */
  mode?: 'add' | 'edit';
  /** Nothing can be changed (a save under way). */
  disabled?: boolean;
  /**
   * Why the bed's kind, sun and what's over it (its shelter) cannot be changed here: a member editing a bed in someone
   * else's garden (B37, useOwnerOnly). Null or absent: they can.
   */
  siteLockedReason?: string | null;
}

/**
 * The bed's questions, all those its kind asks.
 *
 * @param props - BedFactsFieldsProps.
 */
export const BedFactsFields: React.FC<BedFactsFieldsProps> = ({ questions, value, onChange, mode = 'edit', disabled = false, siteLockedReason = null }) => {
  const { reactTranslate: t } = useTranslation();
  /** The kinds' list is open in its sheet (editing). */
  const [choosingKind, setChoosingKind] = React.useState(false);
  if (!questions) return null;
  const asks = questions.questions[value.kind];
  /**
   * The draft with some answers changed.
   *
   * @param patch - The answers to change.
   */
  const set = (patch: Partial<BedDraft>) => onChange({ ...value, ...patch });
  const frostKeys = questions.frostCovers.filter((cover) => asks?.covers.includes(cover));
  const shadeKeys = questions.shadeCovers.filter((cover) => asks?.covers.includes(cover));
  const shadeAsked = shadeKeys.length > 0 && (!asks?.sun || questions.shadeSun.includes(value.sun));
  const covers = value.covers ?? [];
  const inner = value.kind === 'greenhouse';
  const kindNote = t(`locations.facts.kindNote.${value.kind}`);
  const hasNote = kindNote !== `locations.facts.kindNote.${value.kind}`;
  const groups = questions.kindGroups;
  /** The sun and what's over it cannot be changed: a save under way, or the garden owner's alone (B37). */
  const siteDisabled = disabled || Boolean(siteLockedReason);
  /** "What blocks the sun?"'s words (the server's, always sent). */
  const shadeWords = questions.words.shade;
  /**
   * A shade source's line on what it does (locations.facts.shadeSourceSub.*), or null for a source the app has no
   * words for yet.
   *
   * @param source - The source's key.
   */
  const sourceSub = (source: string): string | null => {
    const key = `locations.facts.shadeSourceSub.${source}`;
    const said = t(key);
    return said === key ? null : said;
  };

  /**
   * A frost cover ticked or unticked; nothing ticked is nothing said (null), as an untouched bed.
   *
   * @param cover - The cover.
   */
  const toggleFrost = (cover: BedCover) => {
    const next = covers.includes(cover) ? covers.filter((c) => c !== cover) : [...covers, cover];
    set({ covers: next.length === 0 ? null : next });
  };
  /**
   * Shade cloth chosen: its one, or none (which is said: none on hand).
   *
   * @param choice - A shade cover, or "none".
   */
  const chooseShade = (choice: string) => {
    const rest = covers.filter((c) => !shadeKeys.includes(c));
    set({ covers: choice === 'none' ? rest : [...rest, choice as BedCover] });
  };
  /** The choices of what's over it that this kind asks, as the server lists them (what each sets is the server's). */
  const overChoices = questions.over.filter((choice) => choice.needs === null || asks?.[choice.needs]);
  /** What's over it, from the draft: the choice the server says matches it. */
  const over = questions.over.find((choice) => choice.matches.coverStays === value.coverStays
    && (choice.matches.hoops === undefined || choice.matches.hoops === value.hoops))?.key ?? 'nothing';
  /**
   * What's over it chosen. The server says what a choice sets; all season is plastic on hoops that stays: a tunnel.
   *
   * @param choice - The chosen one, with what it sets.
   */
  const chooseOver = (choice: BedOverChoice) => {
    const plastic = choice.sets.plastic && (asks?.covers.includes('plastic_hoops') ?? false);
    const withoutPlastic = covers.filter((c) => c !== 'plastic_hoops');
    if (choice.sets.plastic) {
      const next = plastic ? [...withoutPlastic, 'plastic_hoops' as BedCover] : withoutPlastic;
      set({ coverStays: choice.sets.coverStays, hoops: choice.sets.hoops, covers: next.length === 0 ? null : next });
      return;
    }
    set({ coverStays: choice.sets.coverStays, hoops: choice.sets.hoops, covers: withoutPlastic.length === 0 ? (value.covers === null ? null : []) : withoutPlastic });
  };

  /** The kinds in their groups, one choice, each with its hint. */
  const kindList = (
    <div role="radiogroup" aria-labelledby="bed-kind-label" className="space-y-1">
      {groups.map((group) => (
        <div key={group.key}>
          {group.key !== 'all' && (
            <p className="mt-3 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t(`locations.facts.kindGroup.${group.key}`)}</p>
          )}
          {group.kinds.map((kind) => (
            <ChoiceRow
              key={kind}
              label={questions.words.kind[kind]}
              hint={t(`locations.facts.kindHint.${kind}`)}
              on={value.kind === kind}
              onChoose={() => { set({ kind: kind as BedKind }); setChoosingKind(false); }}
              disabled={disabled}
            />
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Its kind: listed when adding; one row with Change when editing. */}
      <div className="space-y-1.5">
        <Question id="bed-kind-label" label={t('locations.facts.kindLabel')} />
        {mode === 'add' ? kindList : (
          <button
            type="button"
            onClick={() => setChoosingKind(true)}
            disabled={siteDisabled}
            className="flex min-h-12 w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            <span className="min-w-0">
              <span className="block text-[0.9375rem] font-semibold">{questions.words.kind[value.kind]}</span>
              <span className="block text-xs text-muted-foreground">{t(`locations.facts.kindHint.${value.kind}`)}</span>
            </span>
            <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-primary">
              {t('locations.facts.change')}
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </span>
          </button>
        )}
      </div>
      {mode !== 'add' && (
        <BottomPanel open={choosingKind} onOpenChange={setChoosingKind} title={t('locations.facts.kindLabel')}>
          {kindList}
        </BottomPanel>
      )}

      {/* A kind with fewer questions says why. */}
      {hasNote && <p className="rounded-lg bg-muted/70 px-3 py-2.5 text-sm">{kindNote}</p>}

      {/* In someone else's garden: why the kind, the sun and what's over it are greyed (B37). */}
      {siteLockedReason && mode !== 'add' && (
        <p className="text-sm text-muted-foreground">{siteLockedReason}</p>
      )}

      {asks?.sun && (
        <div>
          <Question id="bed-sun-label" label={t('locations.facts.sunLabel')} />
          <Segmented
            labelId="bed-sun-label"
            options={questions.sun.map((sun) => ({ value: sun, label: questions.words.sun[sun] }))}
            value={value.sun}
            onChoose={(sun) => set({ sun: sun as BedDraft['sun'] })}
            disabled={siteDisabled}
          />
          <p className="mt-1 text-xs text-muted-foreground">{t(`locations.facts.sunHint.${value.sun}`)}</p>
          {/* What blocks the sun: the sun question's second half, so inside its block, not a question of its own. */}
          {shadeWords && shadeSourceAsked(value, questions) && (
            <div className="mt-3 space-y-1">
              <Question id="bed-shade-source-label" label={shadeWords.question} />
              <div role="radiogroup" aria-labelledby="bed-shade-source-label" className="space-y-1">
                {[...questions.shade, null].map((source) => (
                  <ChoiceRow
                    key={source ?? 'notSure'}
                    label={source === null ? shadeWords.notSure : shadeWords[source] ?? source}
                    // Not sure has no second line; a source says what it does (none for one the app has no words for).
                    hint={source === null ? undefined : sourceSub(source)}
                    on={(value.shade ?? null) === source}
                    onChoose={() => set({ shade: source })}
                    disabled={siteDisabled}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {(asks?.hoops || asks?.coverStays) && overChoices.length > 0 && (
        <div className="space-y-1">
          <Question id="bed-over-label" label={t('locations.facts.overLabel')} hint={t('locations.facts.overHint')} />
          <div role="radiogroup" aria-labelledby="bed-over-label" className="space-y-1">
            {overChoices.map((choice) => (
              <ChoiceRow
                key={choice.key}
                label={t(`locations.facts.over.${choice.key}`)}
                // Nothing has no second line; the others say what they mean.
                hint={choice.key === 'nothing' ? undefined : t(`locations.facts.overSub.${choice.key}`)}
                on={over === choice.key}
                onChoose={() => chooseOver(choice)}
                disabled={siteDisabled}
              />
            ))}
          </div>
          {/* Plastic that stays on all season is a tunnel: offered as one, never forced. */}
          {over === 'season' && questions.questions.greenhouse && !siteLockedReason && (
            <button
              type="button"
              onClick={() => set({ kind: 'greenhouse', coverStays: false })}
              disabled={disabled}
              className="ml-11 min-h-11 text-sm font-semibold text-primary underline underline-offset-4"
            >
              {t('locations.facts.makeTunnel')}
            </button>
          )}
        </div>
      )}

      {frostKeys.length > 0 && (
        <div>
          <Question id="bed-frost-label" label={t(inner ? 'locations.facts.coversInner' : 'locations.facts.frostLabel')} hint={t('locations.facts.coversHint')} />
          <div role="group" aria-labelledby="bed-frost-label" className="mt-1 grid grid-cols-1 min-[440px]:grid-cols-2">
            {frostKeys.map((cover) => (
              <CheckRow key={cover} label={questions.words.cover[cover]} on={covers.includes(cover)} onToggle={() => toggleFrost(cover)} disabled={disabled} />
            ))}
          </div>
        </div>
      )}

      {shadeAsked && (
        <div>
          <Question id="bed-shade-label" label={t('locations.facts.shadeLabel')} hint={t('locations.facts.shadeHint')} />
          <Segmented
            labelId="bed-shade-label"
            options={['none', ...shadeKeys].map((key) => ({ value: key, label: t(`locations.facts.shade.${key}`) }))}
            value={shadeKeys.find((key) => covers.includes(key)) ?? (value.covers === null ? null : 'none')}
            onChoose={chooseShade}
            disabled={disabled}
          />
        </div>
      )}

      {asks?.watering && (
        <div>
          <Question id="bed-watering-label" label={t('locations.facts.wateringLabel')} />
          <Segmented
            labelId="bed-watering-label"
            options={questions.watering.map((how) => ({ value: how, label: questions.words.watering[how] }))}
            value={value.watering}
            onChoose={(how) => set({ watering: how as BedDraft['watering'] })}
            disabled={disabled}
          />
        </div>
      )}

      {asks?.wind && (
        <div>
          <Question id="bed-wind-label" label={t('locations.facts.windLabel')} />
          <Segmented
            labelId="bed-wind-label"
            options={questions.wind.map((wind) => ({ value: wind, label: questions.words.wind[wind] }))}
            value={value.wind}
            onChoose={(wind) => set({ wind: wind as BedDraft['wind'] })}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};
